import React, { useMemo } from "react"
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Input,
  Button,
  DropdownTrigger,
  Dropdown,
  DropdownMenu,
  DropdownItem,
  Chip,
  Link,
  Pagination,
} from "@nextui-org/react"

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faAngleDown,
  faDownload,
  faEllipsisVertical,
  faMagnifyingGlass,
} from "@fortawesome/free-solid-svg-icons"
import { exportToCSV, exportToExcel, exportToPDF } from "../utility/utility.js"
import { format } from "date-fns"

const statusColorMap = {
  active: "success",
  paused: "danger",
  vacation: "warning",
}

// const INITIAL_VISIBLE_COLUMNS = ["name","age", "role", "status", "actions"]

// const columns = [
//   { name: "ID", uid: "id", sortable: true },
//   { name: "NAME", uid: "name", sortable: true },
//   { name: "AGE", uid: "age", sortable: true },
//   { name: "ROLE", uid: "role", sortable: true },
//   { name: "TEAM", uid: "team" },
//   { name: "EMAIL", uid: "email" },
//   { name: "STATUS", uid: "status", sortable: true },
//   { name: "ACTIONS", uid: "actions" },
// ]

// const data = [
//   {
//     id: 1,
//     name: "Tony Reichert",
//     role: "CEO",
//     team: "Management",
//     status: "active",
//     age: "29",
//     avatar: "https://i.pravatar.cc/150?u=a042581f4e29026024d",
//   }, 
// ]

const statusOptions = [
  { name: "Active", uid: "active" },
  { name: "Paused", uid: "paused" },
  { name: "Vacation", uid: "vacation" },
]

export const ROW_ACTION_BTN_TYPE = {
  link: "link",
}

export const FOOTER_TYPE = {
  link: "link",
  text: "text",
}

export const COLUMN_TYPE = {
  date: "date",
  link: "link",
  chip: "chip",
}




export default function NextUITable({
  INITIAL_VISIBLE_COLUMNS=[],
  data=[],
  columns=[],
  id="",
  downloadFileName="",
  exportColumns=[],
  topActionBtn = [],
  rowActionBtn = [],
  rowSelection = false,
  downloadDataFooter = {},
  rowMenuActionBtn = [],
}) {
  let index = 0

  const [filterValue, setFilterValue] = React.useState("")
  const [selectedKeys, setSelectedKeys] = React.useState(new Set([]))
  const [visibleColumns] = React.useState(new Set(INITIAL_VISIBLE_COLUMNS))
  const [statusFilter] = React.useState("all")
  const [rowsPerPage, setRowsPerPage] = React.useState(10)
  const [sortDescriptor, setSortDescriptor] = React.useState({})
  const [page, setPage] = React.useState(1)

  const pages = Math.ceil(data.length / rowsPerPage)

  const hasSearchFilter = Boolean(filterValue)

  const headerColumns = React.useMemo(() => {
    if (visibleColumns === "all") return columns

    return columns.filter((column) =>
      Array.from(visibleColumns).includes(column.uid)
    )
  }, [visibleColumns, columns])

  function capitalize(str) {
    return str?.charAt(0)?.toUpperCase() + str?.slice(1)
  }

  const filteredItems = useMemo(() => {
    let filteredData = [...data]

    if (hasSearchFilter) {
      let results = []

      for (let i = 0; i < data.length; i++) {
        for (let prop in data[i]) {
          if (
            data[i].hasOwnProperty(prop) &&
            data[i][prop]
              .toString()
              .toLowerCase()
              .includes(filterValue.toLowerCase())
          ) {
            results.push(data[i])
            break // Break to avoid duplicates
          }
        }
      }
      filteredData = results
    }

    // if (hasColumnFilter) {

    //   let result = filteredData.filter((data) => {
    //     const itemValue = data[columnFilterValue?.key];
    //     let itemType = typeof itemValue;
    //     return data[columnFilterValue?.key].toLowerCase().includes(columnFilterValue?.value.toString().toLowerCase())
    //   }

    //   )
    //   filteredData = result

    // }

    // filteredData = selectFilter({
    //   data: filteredData,
    //   conditions: topFilter,
    // })

    return filteredData
  }, [data, filterValue,  hasSearchFilter])

  const items = React.useMemo(() => {
    const start = (page - 1) * rowsPerPage
    const end = start + rowsPerPage

    return filteredItems.slice(start, end)
  }, [page, filteredItems, rowsPerPage])

  const sortedItems = React.useMemo(() => {
    return [...items].sort((a, b) => {
      const first = a[sortDescriptor.column]
      const second = b[sortDescriptor.column]
      const cmp = first < second ? -1 : first > second ? 1 : 0

      return sortDescriptor.direction === "descending" ? -cmp : cmp
    })
  }, [sortDescriptor, items])

  const renderCell = React.useCallback(
    (row, columnKey, column) => {
      const cellValue = row[columnKey]
    
      if (column && column?.hasOwnProperty("secondCol")) {
        return (
          <div className="flex flex-col">
            <p className="text-bold text-small capitalize">{cellValue}</p>
            <p className="text-bold text-tiny capitalize  text-blue-500 flex flex-row items-center gap-0.5">
              {column?.secondCol?.icon}
              {row[column.secondCol.name]}
            </p>
          </div>
        )
      } else if (column?.hasOwnProperty("type") && column.type === COLUMN_TYPE.chip) {
        return (
          <Chip color="danger" variant="flat" size="sm" radius="sm">
            {cellValue}
          </Chip>
        )
      } else if (column?.hasOwnProperty("type") && column.type === COLUMN_TYPE.date) {
        return format(new Date(cellValue), "dd/MM/yyyy")
      } else if (column?.hasOwnProperty("type") && column.type === COLUMN_TYPE.link) {
        return (
          <Link
            href="#"
            color="primary"
            underline="hover"
            onClick={() => {
              column.onClick(row)
            }}
            className="text-sm flex items-center"
          >
            {cellValue}
          </Link>
        )
      } 
      
      switch (columnKey) {
        case "status":
          return (
            <Chip
              className="capitalize border-none gap-1 text-default-600"
              color={statusColorMap[data.status]}
              size="sm"
              variant="dot"
            >
              {cellValue}
            </Chip>
          )
        case "actions":
          return (
            <div className="relative flex justify-end items-center gap-2">
              {rowActionBtn.map((btn, index) => {
                return (
                  <div key={index}>
                    <Button
                      size="sm"
                      onClick={() => {
                        btn.onClick(row)
                      }}
                      className={`px-3 ${
                        btn && btn?.type === ROW_ACTION_BTN_TYPE.link
                          ? "text-black"
                          : "text-blue-500"
                      }`}
                      variant="light"
                    >
                      {btn.label}
                      {btn.icon}
                    </Button>
                  </div>
                )
              })}
            </div>
          )
        case "menuActions":
          return (
            <Dropdown className="bg-background border-1 border-default-200">
              <DropdownTrigger>
                <Button isIconOnly radius="full" size="sm" variant="light">
                  <FontAwesomeIcon icon={faEllipsisVertical} />
                </Button>
              </DropdownTrigger>
              <DropdownMenu>
                {rowMenuActionBtn.map((item, index) => {
                  return (
                    <DropdownItem
                      key={index}
                      onClick={() => {
                        item.onClick(row)
                      }}
                      startContent={item.icon}
                    >
                      {item.label}
                    </DropdownItem>
                  )
                })}
              </DropdownMenu>
            </Dropdown>
          )
        default:
          return cellValue
      }
    },
    [data.status, rowActionBtn]
  )

  const onRowsPerPageChange = React.useCallback((e) => {
    setRowsPerPage(Number(e.target.value))
    setPage(1)
  }, [])

  const onSearchChange = React.useCallback((value) => {
    if (value) {
      setFilterValue(value)
      setPage(1)
    } else {
      setFilterValue("")
    }
  }, [])

  const addDataFooter = (tempData) => {
    let downloadDataFooter = {}
    if (Object.keys(downloadDataFooter).length > 0) {
      return [...tempData, downloadDataFooter]
    }
    return tempData
  }

  const downloadItems = [
    {
      labelName: "PDF",
      onClick: () => {
        exportToPDF({
          data: addDataFooter(data),
          downloadFileName: downloadFileName,
          downloadDataFooter: downloadDataFooter,
          exportColumns,
          downloadFileheader: "",
        })
      },
    },
    {
      labelName: "CSV",
      onClick: () => {
        exportToCSV({
          data: data,
          downloadFileName: downloadFileName,
          exportColumns,
        })
      },
    },
    {
      labelName: "EXCEL",
      onClick: () => {
        exportToExcel({
          data: data,
          downloadFileName: downloadFileName,
          exportColumns,
        })
      },
    },
  ]

  const topContent = React.useMemo(() => {
    return (
      <div className="flex flex-col gap-4">
        <div className="flex justify-between gap-3 items-end">
          <Input
            isClearable
            classNames={{
              base: "sm:max-w-[44%]",
              inputWrapper: "border-1",
            }}
            placeholder="Search..."
            size="sm"
            startContent={<FontAwesomeIcon icon={faMagnifyingGlass} />}
            value={filterValue}
            variant="bordered"
            onClear={() => setFilterValue("")}
            onValueChange={onSearchChange}
          />
          <div className="flex gap-3">
            <Dropdown>
              <DropdownTrigger className="flex">
                <Button
                  endContent={<FontAwesomeIcon icon={faAngleDown} />}
                  size="sm"
                  variant="flat"
                >
                  Download <FontAwesomeIcon icon={faDownload} />
                </Button>
              </DropdownTrigger>
              <DropdownMenu
                disallowEmptySelection
                aria-label="download"
                closeOnSelect={true}
                selectionMode="single"
              >
                {downloadItems.map((item, index) => (
                  <DropdownItem
                    key={index}
                    className="capitalize"
                    onClick={item.onClick}
                  >
                    {capitalize(item.labelName)}
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </Dropdown>

            {topActionBtn.map((item) => {
              return (
                <div>
                  {item.isShowOnSelection ? (
                    <Button
                      className="bg-foreground text-background"
                      endContent={item.icon}
                      size="sm"
                      isDisabled={
                        !(selectedKeys.size > 0 || selectedKeys === "all")
                      }
                      onClick={() => {
                        item.onClick({ selectedKeys: selectedKeys })
                      }}
                    >
                      {item.label}
                    </Button>
                  ) : (
                    <Button
                      color="primary"
                      endContent={item.icon}
                      size="sm"
                      onClick={item.onClick}
                    >
                      {item.label}
                    </Button>
                  )}
                </div>
              )
            })}
          </div>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-default-400 text-small">
            Total {data.length} data
          </span>
          <label className="flex items-center text-default-400 text-small">
            Rows per page:
            <select
              className="bg-transparent outline-none text-default-400 text-small"
              onChange={onRowsPerPageChange}
            >
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="15">15</option>
            </select>
          </label>
        </div>
      </div>
    )
  }, [
    topActionBtn,
    filterValue,
    onSearchChange,
    onRowsPerPageChange,
    data.length,
    selectedKeys,
  ])

  const bottomContent = React.useMemo(() => {
    return (
      <div className="py-2 px-0 flex-col">
        {/* footer */}
        <table className="mb-3 w-full">
          <tfoot>
            <tr>
              {INITIAL_VISIBLE_COLUMNS.map((visibleCol, i) => {
                const column = columns.find((row) => row.uid === visibleCol)
                return (
                  <td
                    key={i}
                    className={`bg-gray-50 text-sm pl-3 pr-8 rounded  `}
                  >
                    {column?.hasOwnProperty("footer") ? (
                      <span
                        className={`${
                          column?.footer?.type === FOOTER_TYPE.link
                            ? " flex justify-end "
                            : ""
                        }"`}
                      >
                        {column?.footer?.type === FOOTER_TYPE.text ? (
                          <span>
                            {column?.footer?.label} {column?.footer?.value}
                          </span>
                        ) : (
                          <Button
                            size="sm"
                            onClick={column?.footer?.onClick}
                            className={`text-black  `}
                            variant="light"
                          >
                            {column?.footer?.label}
                            {column?.footer?.icon}
                          </Button>
                        )}
                      </span>
                    ) : (
                      ""
                    )}
                  </td>
                )
              })}
            </tr>
          </tfoot>
          <thead className="rounded  bg-gray-100">
            <tr>
              {INITIAL_VISIBLE_COLUMNS.map((visibleCol, i) => {
                const column = columns.find((row) => row.uid === visibleCol)
                return (
                  <th
                    key={i}
                    className="text-sm pl-3 pr-8 text-nowrap py-1 text-left text-gray-500"
                  >
                    {column &&
                      column?.uid.toLowerCase() !== "actions" &&
                      column?.name}
                  </th>
                )
              })}
            </tr>
          </thead>
        </table>
        {/* end of footer */}
        <div className="flex justify-between items-center">
          <Pagination
            isCompact
            showControls
            color="primary"
            isDisabled={hasSearchFilter}
            page={page}
            total={pages}
            variant="flat"
            onChange={setPage}
            className="flex flex-col "
          />
          <p className=" text-small text-default-400">
            {selectedKeys === "all"
              ? "All items selected"
              : `${selectedKeys.size} of ${items.length} selected`}
          </p>
        </div>
      </div>
    )
  }, [
    selectedKeys,
    items.length,
    page,
    pages,
    hasSearchFilter,
    INITIAL_VISIBLE_COLUMNS,
    columns,
  ])

  const classNames = React.useMemo(
    () => ({
      wrapper: ["max-h-[382px]", "max-w-3xl", "overflow-hidden"],
      th: ["p-2", "bg-gray-700","uppercase","text-white"],
      tr: ["hover:bg-gray-200", "even:bg-gray-50"],
      td: [
        // changing the rows border radius
        // first
        "group-data-[first=true]:first:before:rounded-none",
        "group-data-[first=true]:last:before:rounded-none",
        // middle
        "group-data-[middle=true]:before:rounded-none",
        // last
        "group-data-[last=true]:first:before:rounded-none",
        "group-data-[last=true]:last:before:rounded-none",
        "text-sm",
      ],
    }),
    []
  )

  return (
    <div className="">
      <Table
        className="m-0"
        isHeaderSticky
        isCompact
        removeWrapper
        selectionBehavior={rowSelection ? "toggle" : "replace"}
        aria-label="Example table with custom cells, pagination and sorting"
        bottomContent={bottomContent}
        bottomContentPlacement="outside"
        checkboxesProps={{
          classNames: {
            wrapper:
              "after:bg-foreground after:text-background text-background",
          },
        }}
        classNames={classNames}
        selectedKeys={selectedKeys}
        selectionMode="multiple"
        sortDescriptor={sortDescriptor}
        topContent={topContent}
        topContentPlacement="outside"
        onSelectionChange={setSelectedKeys}
        onSortChange={setSortDescriptor}
      >
        <TableHeader columns={headerColumns}>
          {(column, index) => {
            return (
              <TableColumn
                key={column.uid || index}
                align={
                  (column.uid === "actions")
                    ? "end"
                    : "start"
                }
                allowsSorting={column.sortable}
              >
                {column.name}
              </TableColumn>
            )
          }}
        </TableHeader>
        <TableBody emptyContent={"No data found"} items={sortedItems}>
          {(item, i) => {
            index = index + 1
            return (
              <TableRow key={item[id] || index}>
                {(columnKey) => {
                  const column = columns.find((row) => row.uid === columnKey)
                  return (
                    <TableCell
                      className={`${
                        column &&
                        column?.hasOwnProperty("align") &&
                        column.align === "right"
                          ? "text-right"
                          : ""
                      }`}
                    >
                      {renderCell(item, columnKey, column)}
                    </TableCell>
                  )
                }}
              </TableRow>
            )
          }}
        </TableBody>
      </Table>
    </div>
  )
}
